/**
 * Image generation request detection
 * Detects if user wants to generate an image based on keywords and patterns
 */

const IMAGE_KEYWORDS = [
  'draw', 'paint', 'sketch', 'illustrate', 'create',
  'generate', 'make', 'design', 'render',
  'image', 'picture', 'photo', 'artwork', 'illustration',
  'show me', 'visualize', 'depict'
];

const IMAGE_PHRASES = [
  'an image of',
  'a picture of',
  'a drawing of',
  'artwork of',
  'illustration of',
  'generate an image',
  'create an image',
  'make an image',
  'draw me',
  'show me a picture',
  'can you draw',
  'can you create',
  'generate a picture'
];

/**
 * Detects if a message is requesting image generation
 * @param {string} message - User's message
 * @returns {boolean} - True if image generation is likely requested
 */
export function isImageGenerationRequest(message) {
  if (!message || typeof message !== 'string') return false;

  const lowerMessage = message.toLowerCase().trim();

  // Check for exact phrase matches (high confidence)
  for (const phrase of IMAGE_PHRASES) {
    if (lowerMessage.includes(phrase)) {
      return true;
    }
  }

  // Check for keyword combinations (medium confidence)
  // Must have at least 2 keywords or 1 keyword + question mark
  const keywordMatches = IMAGE_KEYWORDS.filter(keyword =>
    lowerMessage.includes(keyword)
  );

  if (keywordMatches.length >= 2) {
    return true;
  }

  if (keywordMatches.length === 1 && lowerMessage.includes('?')) {
    return true;
  }

  return false;
}

/**
 * Extracts the actual image prompt from user's message
 * Removes command words like "draw", "create", etc.
 * @param {string} message - User's message
 * @returns {string} - Cleaned prompt for image generation
 */
export function extractImagePrompt(message) {
  let prompt = message.trim();

  // Remove common command prefixes
  const prefixes = [
    /^(please\s+)?draw\s+(me\s+)?/i,
    /^(please\s+)?create\s+(an?\s+)?(image|picture|drawing)\s+(of\s+)?/i,
    /^(please\s+)?generate\s+(an?\s+)?(image|picture|drawing)\s+(of\s+)?/i,
    /^(please\s+)?make\s+(an?\s+)?(image|picture|drawing)\s+(of\s+)?/i,
    /^(please\s+)?show\s+me\s+(an?\s+)?(image|picture|drawing)\s+(of\s+)?/i,
    /^(can\s+you\s+)?(please\s+)?draw\s+/i,
    /^(can\s+you\s+)?(please\s+)?create\s+/i,
    /^(can\s+you\s+)?(please\s+)?generate\s+/i
  ];

  for (const prefix of prefixes) {
    prompt = prompt.replace(prefix, '');
  }

  return prompt.trim();
}
